import React, { useEffect } from 'react';
import { Form, Input, Modal } from 'antd';

const HTTPS_RE = /^https:\/\/\S+/i;

function validateServiceUrlLines(_, value) {
    if (!value || !value.trim()) {
        return Promise.reject(new Error('At least one https URL is required'));
    }
    const lines = value.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    for (const line of lines) {
        if (line === '*') {
            return Promise.reject(new Error('Wildcard is not allowed.'));
        }
        if (!HTTPS_RE.test(line)) {
            return Promise.reject(new Error(`"${line}" must start with https://`));
        }
    }
    return Promise.resolve();
}

function validateAppIcon(_, value) {
    if (!value) return Promise.resolve();
    if (!HTTPS_RE.test(value)) {
        return Promise.reject(new Error('app_icon must be an https URL'));
    }
    return Promise.resolve();
}

export default function MyAppForm({ open, initial, onSubmit, onCancel, confirmLoading }) {
    const [form] = Form.useForm();
    const isEdit = !!initial;

    useEffect(() => {
        if (!open) return;
        if (initial) {
            form.setFieldsValue({
                app_name: initial.app_name || '',
                description: initial.description || '',
                service_url: (initial.service_url || '').split('::').join('\n'),
                app_icon: initial.app_icon || '',
                notes: initial.notes || '',
            });
        } else {
            form.resetFields();
        }
    }, [open, initial, form]);

    const handleOk = () => {
        form.validateFields().then((values) => {
            const lines = values.service_url
                .split(/\r?\n/)
                .map((s) => s.trim())
                .filter(Boolean);
            onSubmit({
                app_name: values.app_name.trim(),
                description: values.description || '',
                service_url: lines.join('::'),
                app_icon: values.app_icon || null,
                notes: values.notes || null,
            });
        });
    };

    return (
        <Modal
            title={isEdit ? 'Edit Application' : 'Register Application'}
            open={open}
            onOk={handleOk}
            onCancel={onCancel}
            confirmLoading={confirmLoading}
            okText={isEdit ? 'Save' : 'Register'}
            destroyOnClose
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label="Application name"
                    name="app_name"
                    rules={[
                        { required: true, message: 'Application name is required' },
                        { max: 100, message: 'Max 100 characters' },
                    ]}
                >
                    <Input placeholder="My Service" />
                </Form.Item>

                <Form.Item label="Description" name="description">
                    <Input.TextArea rows={2} />
                </Form.Item>

                <Form.Item
                    label="Service URL(s)"
                    name="service_url"
                    tooltip="One https:// URL per line. A ticket will only be issued to URLs that start with one of these prefixes."
                    rules={[{ validator: validateServiceUrlLines }]}
                >
                    <Input.TextArea
                        rows={3}
                        placeholder={'https://example.chula.ac.th/login\nhttps://alt.example.chula.ac.th/sso'}
                    />
                </Form.Item>

                <Form.Item
                    label="App icon URL"
                    name="app_icon"
                    rules={[{ validator: validateAppIcon }]}
                >
                    <Input placeholder="https://..." />
                </Form.Item>

                <Form.Item label="Notes" name="notes">
                    <Input.TextArea rows={2} />
                </Form.Item>

                {isEdit && (
                    <div style={{ color: '#faad14' }}>
                        Changing Service URL(s) on an approved app will send it back to pending review.
                    </div>
                )}
            </Form>
        </Modal>
    );
}
